import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  FormControl,
  Select,
} from "@mui/material";

import GooglePlacesAutocomplete from "./LocationAutocomplete";
import { isBusinessEmail } from "@/helpers/email.helper";
import vendorService from "@/service/vender.service";


const vendorTypes = [
  "Realestate Agent",
  "Conveyancer/Solicitor",
  "Building & Pest inspector",
  "Buyers agent",
  "Property Manager",
  "Mortgage Broker",
  "Accountant",
  "Building Surveyor"
];

const BasicInfoStep = ({
  formData,
  updateFormData,
  onAddressSelect,
  setIsValid,
  errors,
  setErrors,
  setServicesByType
}) => {
  useEffect(() => {
    // Set validation state based on current errors
    const isValid = Object.values(errors).every((error) => error === "");
    setIsValid(isValid); // Pass validation status back to parent
  }, [errors]);

  useEffect(() => {
    const fetchServices = async () => {
      if (formData?.vendor_type) {
        const services = await vendorService.fetchServicesByType(formData.vendor_type);
        setServicesByType(services);
      }
    };
    fetchServices();
  }, [formData?.vendor_type]);


  const isGenericEmail = (email) => {
    const genericDomains = [
      "gmail.com",
      "yahoo.com",
      "hotmail.com",
      "outlook.com",
      "aol.com",
      "icloud.com",
    ];
    const domain = email.split("@")[1];
    return genericDomains.includes(domain);
  };


  // Validation function for each field
  const validateField = (name, value) => {
    let errorMessage = "";
    switch (name) {
      case "vendor_type":
        if (value === "" || value === "select") {
          errorMessage = "Vendor type is required";
        }
        break;
      case "full_name":
        if (!value.trim()) {
          errorMessage = "Full name is required";
        }
        break;
      case "business_name":
        if (!value.trim()) {
          errorMessage = "Business name is required";
        }
        break;
      case "professional_title":
        if (!value.trim()) {
          errorMessage = "Professional title is required";
        }
        break;
      case "work_email":
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!value.trim()) {
          errorMessage = "Work email is required";
        } else if (!emailRegex.test(value)) {
          errorMessage = "Please enter a valid email address";
        } else if (!isBusinessEmail(value)) {
          errorMessage = "Please use a business email address";
        }
        break;
      case "phone_number":
        const phoneRegex = /^[0-9]{10,11}$/;
        if (!value.trim()) {
          errorMessage = "Phone number is required";
        } else if (!phoneRegex.test(value)) {
          errorMessage = "Please enter a valid phone number";
        }
        break;
      case "address":
        if (!value) {
          errorMessage = "address is required";
        }
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
    return errorMessage;
  };

  // Handle input change with validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value.trimStart();
    if (name) {
      updateFormData(name, updatedValue);
      validateField(name, updatedValue); // Validate the field
    }
  };

  // const handleSelectChange = (e) => {
  //   const { name, value } = e.target;
  //   if (name) {
  //     updateFormData(name, value);
  //     validateField(name, value); // Validate the field
  //   }
  // };

  const handleSelectChange = async (e) => {
    const { name, value } = e.target;
  
    if (name) {
      updateFormData(name, value);
      validateField(name, value);
      if (name === "vendor_type") {
        try {
          const services = await vendorService.fetchServicesByType(value);
          setServicesByType(services); 
        } catch (err) {
          console.error("Failed to load services:", err.message);
        }
      }
    }
  };

  const locationSelect = async ({
    address,
    latitude,
    longitude,
    city,
    postalCode,
  }) => {
    updateFormData((prevData) => ({
      ...prevData,
      ["address"]: address,
      ["post_code"]: postalCode,
    }));

    // updateFormData("address", address);
    await onAddressSelect({ address, latitude, longitude, city, postalCode });
    validateField("address", address); // Validate the field
    // updateFormData("post_code", postalCode);

    // setLocationData({ address, latitude, longitude, city, postalCode });
  };

  return (
    <Box sx={{ padding: { xs: "16px", sm: "32px" } }}>
      <Grid container spacing={3}>
        {/* Vendor Type */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Vendor Type<span style={{ color: "red" }}>*</span>
          </Typography>
          <FormControl fullWidth variant="outlined">
            <Select
              name="vendor_type"
              value={formData?.vendor_type || ""}
              onChange={handleSelectChange}
              displayEmpty
              sx={{
                borderRadius: 1,
                backgroundColor: "rgba(211, 211, 211, 0.18)",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(211, 211, 211, 0.23)",
                  borderRadius: "12px",
                },
                "& .MuiSelect-select": {
                  borderRadius: "12px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {vendorTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.vendor_type && (
              <Typography color="error" variant="body2">
                {errors.vendor_type}
              </Typography>
            )}
          </FormControl>
        </Grid>
        {/* Full Name */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Full Name<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            name="full_name"
            value={formData?.full_name}
            onChange={handleChange}
            placeholder="Full Name"
            variant="outlined"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}
          />
          {errors.full_name && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }}
            >
              {errors.full_name}
            </Typography>
          )}
        </Grid>
        {/* Business Name */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Business Name<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            name="business_name"
            value={formData?.business_name}
            onChange={handleChange}
            placeholder="Doe Mortgage Solutions"
            variant="outlined"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}
          />
          {errors.business_name && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }} // Font weight reduced here
            >
              {errors.business_name}
            </Typography>
          )}
        </Grid>
        {/* Professional Title */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Professional Title<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            name="professional_title"
            value={formData?.professional_title}
            onChange={handleChange}
            placeholder="Certified Mortgage Broker"
            variant="outlined"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}
          />
          {errors.professional_title && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }} // Font weight reduced here
            >
              {errors.professional_title}
            </Typography>
          )}
        </Grid>
        {/* Work Email */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Work Email<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            name="work_email"
            value={formData?.work_email}
            onChange={handleChange}
            placeholder="john.doe@email.com"
            variant="outlined"
            type="email"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}
          />
          {errors.work_email && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }} // Font weight reduced here
            >
              {errors.work_email}
            </Typography>
          )}
        </Grid>
        {/* Phone Number */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Phone Number<span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            name="phone_number"
            value={formData?.phone_number}
            onChange={handleChange}
            placeholder="91234567890"
            variant="outlined"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}
          />
          {errors.phone_number && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }} // Font weight reduced here
            >
              {errors.phone_number}
            </Typography>
          )}
        </Grid>
        {/* Location */}
        {/* <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
            Location:
          </Typography>
          <TextField
            fullWidth
            name="address"
            value={formData?.address}
            onChange={handleChange}
            placeholder="Sydney, Aus"
            variant="outlined"
            sx={{
              backgroundColor: "rgba(211, 211, 211, 0.18)",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(211, 211, 211, 0.23)",
                borderRadius: "12px",
              },
            }}

          />
          {errors.address && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }} // Font weight reduced here
            >
              {errors.address}
            </Typography>
          )}
        </Grid> */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>
          Postcode / Suburb:<span style={{ color: "red" }}>*</span>
          </Typography>

          <GooglePlacesAutocomplete
            onAddressSelect={locationSelect}
            prefilledAddress={formData?.address}
          />

          {errors.address && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 400, marginTop: 1 }}
            >
              {errors.address}
            </Typography>
          )}
        </Grid>{" "}
        {/* Correctly closed the Grid item */}
      </Grid>
    </Box>
  );
};

export default BasicInfoStep;
